'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHiveIconSmallLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom';
import Price from '@/app/_components/Price';
import { useHelpWidget } from '../HelpWidgetContext';
import { placeholder as inputPlaceholder } from '@/app/_lib/config/shopping-assistant-config';
const HelpAIClaudio = () => {
  const {
    aiMessages,
    setAiMessages,
    aiSessionId,
    setAiSessionId,
    currentPanel,
    clearAiConversation,
    minimizeWidget
  } = useHelpWidget();

  // Initialize session ID if not already set
  useEffect(() => {
    if (!aiSessionId) {
      setAiSessionId(crypto.randomUUID());
    }
  }, [aiSessionId, setAiSessionId]);
  const messages = aiMessages;
  const setMessages = setAiMessages;
  const sessionId = aiSessionId || '';
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Extract products and variants from messages for side panel - newest first, in order of appearance
  // Note: designers and brands are rendered inline in the message, not in the panel
  const panelItems = useMemo(() => {
    const items = [];
    const seenProductIds = new Set();
    const seenVariantIds = new Set();
    const productRegex = /```product\s*\n?([\s\S]*?)```/g;
    const variantRegex = /```variant\s*\n?([\s\S]*?)```/g;

    // Iterate messages in reverse to get newest items first
    const reversedMessages = [...messages].reverse();
    reversedMessages.forEach(message => {
      if (message.role === 'assistant') {
        // Find all matches with their positions to maintain order within message
        const matches = [];
        let match;
        while ((match = productRegex.exec(message.content)) !== null) {
          try {
            const data = JSON.parse(match[1].trim());
            if (!seenProductIds.has(data.id)) {
              seenProductIds.add(data.id);
              matches.push({
                type: 'product',
                data,
                index: match.index
              });
            }
          } catch (e) {/* skip */}
        }
        while ((match = variantRegex.exec(message.content)) !== null) {
          try {
            const data_0 = JSON.parse(match[1].trim());
            if (!seenVariantIds.has(data_0.variant_id)) {
              seenVariantIds.add(data_0.variant_id);
              matches.push({
                type: 'variant',
                data: data_0,
                index: match.index
              });
            }
          } catch (e_0) {/* skip */}
        }

        // Sort by position within message, then add to items
        matches.sort((a, b) => a.index - b.index);
        items.push(...matches);
      }
    });
    return items;
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const handleSubmit = async e_1 => {
    e_1.preventDefault();
    const trimmedInput = input.trim();
    if (!trimmedInput || isLoading) return;
    const userMessage = {
      role: 'user',
      content: trimmedInput
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messages: [...messages, userMessage],
          sessionId
        })
      });
      if (!response.ok) {
        throw new Error('Failed to get response');
      }
      const data_1 = await response.json();

      // Clear conversation if requested
      if (data_1.clearConversation) {
        clearAiConversation();
        return;
      }
      setMessages(prev_1 => [...prev_1, {
        role: 'assistant',
        content: data_1.message
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev_0 => [...prev_0, {
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Please try again or contact us at 1.866.663.4483 for assistance."
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyDown = e_2 => {
    if (e_2.key === 'Enter' && !e_2.shiftKey) {
      e_2.preventDefault();
      handleSubmit(e_2);
    }
  };
  const hasItems = panelItems.length > 0;
  const hasVariants = panelItems.some(item => item.type === 'variant');
  const [mounted, setMounted] = useState(false);
  const [productPanelRight, setProductPanelRight] = useState(0);

  // Calculate widget width (mirrors HelpWidget.js logic)
  const getWidgetWidth = () => {
    if (typeof window === 'undefined') return 700;
    const vw = window.innerWidth;
    if (vw >= 1280) return Math.min(vw * 0.4, 700);
    if (vw >= 1024) return Math.min(vw * 0.5, 700);
    if (vw >= 768) return Math.min(vw * 0.7, 700);
    // Below md: fill nearly full width
    return vw - 32; // 16px margin on each side
  };
  useEffect(() => {
    setMounted(true);
    const updatePosition = () => {
      const widgetWidth = getWidgetWidth();
      // Get site padding from CSS variable (convert rem to px)
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const sitePaddingValue = getComputedStyle(document.documentElement).getPropertyValue('--site-padding').trim();
      const sitePadding = parseFloat(sitePaddingValue) * (sitePaddingValue.includes('rem') ? rootFontSize : 1);
      // Widget is at right: site-padding, product panel needs small gap
      setProductPanelRight(widgetWidth + sitePadding + 4);
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, []);

  // Product/variant panel rendered via portal, positioned to the left of the widget
  // Only show when AI assistant is the active panel (close immediately when switching away)
  // Hidden on mobile - products shown inline in messages instead
  const productPanel = hasItems && mounted && currentPanel === 'ai_assistant' ? createPortal(<div className="hidden md:flex fixed bottom-4 w-52 bg-white/95 backdrop-blur-sm border border-gray-10 rounded-lg z-[999] flex-col shadow-xl animate-slide-up" style={{
    right: `${productPanelRight}px`,
    maxHeight: 'calc(100vh - 2rem)',
    height: '600px'
  }}>
            <div className="px-2 py-1.5 border-b border-gray-10 bg-gray-3 rounded-t-lg flex items-center justify-between">
                <span className="text-2xs font-medium text-dark uppercase tracking-wide">
                    {hasVariants ? 'Options' : 'Products'} ({panelItems.length})
                </span>
                <span className="text-3xs text-light">click to view</span>
            </div>
            <div className="flex-1 overflow-y-auto scrollbar scrollbar-y" role="region" aria-label="Product results">
                {panelItems.map((item_0, index) => item_0.type === 'variant' ? <VariantCard key={item_0.data.variant_id || index} variant={item_0.data} /> : <ProductCard key={item_0.data.id || index} product={item_0.data} />)}
            </div>
            <div className="px-2 py-2 border-t border-gray-10 bg-gray-3 rounded-b-lg">
                <button onClick={clearAiConversation} className="w-full px-3 py-1.5 text-2xs text-light hover:text-dark transition-colors">
                    Clear
                </button>
            </div>
        </div>, document.body) : null;
  return <div className="flex flex-col h-full" role="region" aria-label="Shopping Assistant chat">
            {/* Chat Area - Full width */}
            <div className="flex-1 overflow-y-auto scrollbar scrollbar-y" role="log" aria-live="polite">
                <div className="p-3 space-y-2">
                    {messages.map((message_0, index_0) => <Message key={index_0} message={message_0} onMobileLinkClick={minimizeWidget} />)}
                    {isLoading && <LoadingIndicator />}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Action Bar */}
            {messages.length > 1 && <div className="px-2 py-1.5 border-t border-gray-10 bg-gray-3 flex items-center">
                    <button type="button" onClick={clearAiConversation} className="text-2xs text-light hover:text-dark transition-colors">
                        Clear conversation
                    </button>
                </div>}

            {/* Input Area */}
            <form onSubmit={handleSubmit} className="p-2 border-t border-gray-10 bg-white">
                <label htmlFor="chat-input" className="sr-only">Type your message</label>
                <div className="flex gap-2">
                    <input ref={inputRef} id="chat-input" type="text" value={input} onChange={e_3 => setInput(e_3.target.value)} onKeyDown={handleKeyDown} placeholder={inputPlaceholder} className="flex-1 min-w-0 px-3 py-2 text-xl lg:text-sm border border-gray-10 rounded-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary" disabled={isLoading} />
                    <button type="submit" disabled={isLoading || !input.trim()} className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-sm disabled:opacity-50 shrink-0">
                        Send
                    </button>
                </div>
            </form>

            {/* Product panel rendered outside widget via portal */}
            {productPanel}
        </div>;
};
const LoadingIndicator = () => {
  const $ = _c(7);
  const [seconds, setSeconds] = useState(0);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const interval = setInterval(() => {
        setSeconds(_temp);
      }, 1000);
      return () => clearInterval(interval);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="text-2xs font-medium text-primary mb-0.5">Claudio</div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="inline-block w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />;
    t4 = <span className="text-xs">Thinking...</span>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== seconds) {
    t5 = <div>{t2}<div className="rounded-lg px-3 py-2 bg-gray-3"><div className="flex items-center gap-2 text-sm text-light">{t3}{t4}<span className="text-xs text-extra-light">{seconds}s</span></div></div></div>;
    $[5] = seconds;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};
const Message = t0 => {
  const $ = _c(31);
  const {
    message,
    onMobileLinkClick
  } = t0;
  const isUser = message.role === "user";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /```designer\s*\n?([\s\S]*?)```/g;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const designerRegex = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = /```brand\s*\n?([\s\S]*?)```/g;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const brandRegex = t2;
  let designerData = null;
  let brandData = null;
  let inlineProducts;
  let inlineVariants;
  let t3;
  if ($[2] !== message.content) {
    inlineProducts = [];
    inlineVariants = [];
    let match;
    if ((match = designerRegex.exec(message.content)) !== null) {
      ;
      try {
        designerData = JSON.parse(match[1].trim());
      } catch (t4) {
        const e = t4;
      }
    }
    if ((match = brandRegex.exec(message.content)) !== null) {
      ;
      try {
        brandData = JSON.parse(match[1].trim());
      } catch (t4) {
        const e_0 = t4;
      }
    }
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = /```product\s*\n?([\s\S]*?)```/g;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    const productRegex2 = t4;
    while ((match = productRegex2.exec(message.content)) !== null) {
      ;
      try {
        inlineProducts.push(JSON.parse(match[1].trim()));
      } catch (t5) {
        const e_1 = t5;
      }
    }
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = /```variant\s*\n?([\s\S]*?)```/g;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    const variantRegex2 = t5;
    while ((match = variantRegex2.exec(message.content)) !== null) {
      ;
      try {
        inlineVariants.push(JSON.parse(match[1].trim()));
      } catch (t6) {
        const e_2 = t6;
      }
    }
    const getTextContent = _temp2;
    t3 = getTextContent(message.content);
    $[2] = message.content;
    $[3] = inlineProducts;
    $[4] = inlineVariants;
    $[5] = t3;
  } else {
    inlineProducts = $[3];
    inlineVariants = $[4];
    t3 = $[5];
  }
  const textContent = t3;
  const entityData = designerData || brandData;
  designerData ? "designer" : brandData ? "brand" : null;
  const hasInlineItems = inlineProducts.length > 0 || inlineVariants.length > 0;
  if (!textContent && !entityData && !hasInlineItems) {
    return null;
  }
  const t4 = `text-2xs font-medium mb-0.5 ${isUser ? "text-dark" : "text-primary"}`;
  const t5 = isUser ? "You" : "Claudio";
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = `rounded-lg px-3 py-2 ${isUser ? "bg-gray-5" : "bg-gray-3"}`;
  let t8;
  if ($[11] !== entityData) {
    t8 = entityData && entityData.image && <Link href={entityData.url || "#"} className="float-left mr-3 mb-1"><Image src={entityData.image} alt={entityData.name || ""} width={80} height={80} className="w-20 h-20 object-cover rounded-sm" /></Link>;
    $[11] = entityData;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== textContent) {
    t9 = <div className="text-sm text-black leading-snug whitespace-pre-line">{textContent}</div>;
    $[13] = textContent;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== entityData) {
    t10 = entityData && <div className="clear-both" />;
    $[15] = entityData;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== hasInlineItems || $[18] !== inlineProducts || $[19] !== inlineVariants || $[20] !== onMobileLinkClick) {
    t11 = hasInlineItems && <div className="md:hidden mt-2 pt-2 border-t border-gray-10"><div className="flex flex-wrap gap-1.5">{inlineProducts.map((product, idx) => <InlineProductCard key={product.id || idx} product={product} onClick={onMobileLinkClick} />)}{inlineVariants.map((variant, idx_0) => <InlineVariantCard key={variant.variant_id || idx_0} variant={variant} onClick={onMobileLinkClick} />)}</div></div>;
    $[17] = hasInlineItems;
    $[18] = inlineProducts;
    $[19] = inlineVariants;
    $[20] = onMobileLinkClick;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t12 = <div className={t7}>{t8}{t9}{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t6) {
    t13 = <div>{t6}{t12}</div>;
    $[28] = t12;
    $[29] = t6;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
};
const ProductCard = t0 => {
  const $ = _c(23);
  const {
    product
  } = t0;
  const imageUrl = product.image || null;
  const t1 = product.url || "#";
  let t2;
  if ($[0] !== imageUrl || $[1] !== product.name) {
    t2 = imageUrl && <div className="shrink-0 overlay-block"><Image src={imageUrl} alt={product.name || ""} width={48} height={48} className="w-12 h-12 object-contain scale-100 transition-transform duration-300 group-hover:scale-110" /></div>;
    $[0] = imageUrl;
    $[1] = product.name;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== product.name) {
    t3 = <div className="capitalize font-medium text-2xs mb-0.5 line-clamp-2">{product.name}</div>;
    $[3] = product.name;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== product.price) {
    t4 = <div className="capitalize font-light text-3xs text-neutral-600 mb-0.5"><Price amount={product.price} /></div>;
    $[5] = product.price;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== product.brand || $[8] !== product.designer) {
    t5 = product.brand && <span className={`capitalize font-medium ${product.designer ? "border-r-1 pr-1 mr-1" : ""}`}>{product.brand}</span>;
    $[7] = product.brand;
    $[8] = product.designer;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== product.designer) {
    t6 = product.designer && <span className="capitalize text-light">{product.designer}</span>;
    $[10] = product.designer;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="flex items-center text-3xs">{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t7) {
    t8 = <div className="flex-1 min-w-0">{t3}{t4}{t7}</div>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t1 || $[20] !== t2 || $[21] !== t8) {
    t9 = <Link href={t1} className="group flex items-start gap-2 p-2 border-b border-gray-8 last:border-b-0 hover:bg-gray-3 transition-colors">{t2}{t8}</Link>;
    $[19] = t1;
    $[20] = t2;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
const VariantCard = t0 => {
  const $ = _c(26);
  const {
    variant
  } = t0;
  const imageUrl = variant.image || null;
  const variantUrl = variant.url ? `${variant.url}?variant_id=${variant.variant_id}` : "#";
  let t1;
  if ($[0] !== imageUrl || $[1] !== variant.name || $[2] !== variant.option) {
    t1 = <div className="shrink-0 w-12 h-12 flex items-center justify-center">{imageUrl ? <div className="overlay-block"><Image src={imageUrl} alt={variant.option || variant.name || ""} width={48} height={48} className="w-12 h-12 object-contain scale-100 transition-transform duration-300 group-hover:scale-110" /></div> : <FontAwesomeIcon icon={faHiveIconSmallLight} className="w-6 h-6 text-gray-20" />}</div>;
    $[0] = imageUrl;
    $[1] = variant.name;
    $[2] = variant.option;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = variant.option || variant.name;
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="capitalize font-medium text-2xs mb-0.5 line-clamp-2">{t2}</div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== variant.price) {
    t4 = <div className="capitalize font-light text-3xs text-neutral-600 mb-0.5"><Price amount={variant.price} /></div>;
    $[6] = variant.price;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== variant.brand) {
    t5 = variant.brand && <span className="capitalize font-medium">{variant.brand}</span>;
    $[8] = variant.brand;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== variant.in_stock) {
    t6 = variant.in_stock === false && <span className="text-extra-light">• Out of stock</span>;
    $[10] = variant.in_stock;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== variant.quick_ship) {
    t7 = variant.quick_ship && <span className="text-green-600">• Quick ship</span>;
    $[12] = variant.quick_ship;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t6 || $[16] !== t7) {
    t8 = <div className="flex items-center gap-1 text-3xs">{t5}{t6}{t7}</div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t3 || $[19] !== t4 || $[20] !== t8) {
    t9 = <div className="flex-1 min-w-0">{t3}{t4}{t8}</div>;
    $[18] = t3;
    $[19] = t4;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t1 || $[23] !== t9 || $[24] !== variantUrl) {
    t10 = <Link href={variantUrl} className="group flex items-start gap-2 p-2 border-b border-gray-8 last:border-b-0 hover:bg-gray-3 transition-colors">{t1}{t9}</Link>;
    $[22] = t1;
    $[23] = t9;
    $[24] = variantUrl;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
};

// Compact inline product card for mobile chat display
const InlineProductCard = t0 => {
  const $ = _c(13);
  const {
    product,
    onClick
  } = t0;
  const imageUrl = product.image || null;
  const t1 = product.url || "#";
  let t2;
  if ($[0] !== imageUrl || $[1] !== product.name) {
    t2 = imageUrl && <div className="w-full aspect-square mb-1 overlay-block"><Image src={imageUrl} alt={product.name || ""} width={64} height={64} className="w-full h-full object-contain" /></div>;
    $[0] = imageUrl;
    $[1] = product.name;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== product.name) {
    t3 = <div className="text-3xs font-medium line-clamp-2 leading-tight capitalize">{product.name}</div>;
    $[3] = product.name;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== product.price) {
    t4 = <div className="text-3xs text-light mt-0.5"><Price amount={product.price} /></div>;
    $[5] = product.price;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onClick || $[8] !== t1 || $[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <Link href={t1} onClick={onClick} className="group flex flex-col w-[72px] p-1 rounded bg-white border border-gray-8 hover:border-gray-15 transition-colors">{t2}{t3}{t4}</Link>;
    $[7] = onClick;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};

// Compact inline variant card for mobile chat display
const InlineVariantCard = t0 => {
  const $ = _c(14);
  const {
    variant,
    onClick
  } = t0;
  const imageUrl = variant.image || null;
  const variantUrl = variant.url ? `${variant.url}?variant_id=${variant.variant_id}` : "#";
  let t1;
  if ($[0] !== imageUrl || $[1] !== variant.name || $[2] !== variant.option) {
    t1 = <div className="w-full aspect-square mb-1 flex items-center justify-center">{imageUrl ? <div className="overlay-block w-full h-full"><Image src={imageUrl} alt={variant.option || variant.name || ""} width={64} height={64} className="w-full h-full object-contain" /></div> : <FontAwesomeIcon icon={faHiveIconSmallLight} className="w-6 h-6 text-gray-20" />}</div>;
    $[0] = imageUrl;
    $[1] = variant.name;
    $[2] = variant.option;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = variant.option || variant.name;
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="text-3xs font-medium line-clamp-2 leading-tight capitalize">{t2}</div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== variant.price) {
    t4 = <div className="text-3xs text-light mt-0.5"><Price amount={variant.price} /></div>;
    $[6] = variant.price;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onClick || $[9] !== t1 || $[10] !== t3 || $[11] !== t4 || $[12] !== variantUrl) {
    t5 = <Link href={variantUrl} onClick={onClick} className="group flex flex-col w-[72px] p-1 rounded bg-white border border-gray-8 hover:border-gray-15 transition-colors">{t1}{t3}{t4}</Link>;
    $[8] = onClick;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
    $[12] = variantUrl;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
export default HelpAIClaudio;
function _temp(s) {
  return s + 1;
}
function _temp2(content) {
  return content.replace(/```product\s*\n?[\s\S]*?```/g, "").replace(/```variant\s*\n?[\s\S]*?```/g, "").replace(/```designer\s*\n?[\s\S]*?```/g, "").replace(/```brand\s*\n?[\s\S]*?```/g, "").replace(/\n{3,}/g, "\n\n").replace(/^\n+|\n+$/g, "").trim();
}